/**
 * HELM v2 shared contract (A7Y-3870, phase 1B — read-only build).
 *
 * Every HELM type and constant lives here, and ONLY here (plan D1). This
 * file carries NO `import` statements on purpose: a7y-admin reaches it
 * through a path alias straight to this file (never through the
 * `@a7y/platform-health` package export), so anything imported here would
 * have to resolve inside admin's Vite/Vitest/tsc graph too. Every other
 * HELM module in this package, and every consumer, imports types and
 * constants from here rather than re-declaring them — see plan §12 check 1
 * (`rg "interface Helm" a7y-admin a7y-api scripts` must return nothing).
 *
 * Firestore rejects an array nested directly inside an array. No shape below
 * does that, and the validators in `./validate.ts` reject any value that
 * would.
 */

export const HELM_SCHEMA_VERSION = 1 as const;
export const HELM_TIME_ZONE = 'Europe/Madrid' as const;
export const HELM_REFRESH_EVENT = 'helm-refresh' as const;
export const HELM_COLLECTIONS = {
  runs: 'helm_runs',
  actions: 'helm_actions',
  config: 'helm_config',
  writebacks: 'helm_writebacks',
} as const;
/** The roster file in a7y-ai-dev that the write-back edits (A7Y-4733). */
export const HELM_ROSTER_PATH = 'marketing/growth/helm-roster.md' as const;
/** The pipeline file in a7y-ai-dev; the CRM snapshot renders it beside the roster (A7Y-4976). */
export const HELM_PIPELINE_PATH = 'marketing/growth/pipeline.md' as const;
/** The read-only file in a7y-ai-dev the CRM snapshot writes from HELM's current run, which the morning card copies (A7Y-5882). */
export const HELM_TODAY_PATH = 'marketing/growth/helm-today.md' as const;
export const HELM_OWNER = {
  linearUserId: '09a39c74-d868-4f12-aa4f-4d17384470a6',
  slackUserId: 'U0AP407TXRV',
  email: 'yevhen@apptonomy.ai',
} as const;
export const HELM_DEPLOYED_STATES = {
  onStaging: '74f43d7f-6557-488d-b128-08dcd492e2fe',
  onProd: 'ede35510-7c9c-4a7f-b620-eeacf4a10ef2',
} as const;
export const HELM_JUDGMENT_STATES = {
  triage: '6f9f33de-9263-4dd9-8823-21eccc9b6486',
  todo: 'd5de96b0-98f2-418b-86da-ef1d68cb9fbf',
  blocked: '0f9c47c9-371d-4ec8-b613-23d3219f4db3',
  underDiscussion: '3e8adef2-3c94-4bd5-b7ac-fbcc7b1022e6',
  scheduled: '953a09d2-51dd-414c-b0c0-3f24c4558989',
} as const;
/** The `HELM_JUDGMENT_STATES`, named as the page and the snapshot print them (A7Y-5882). */
export const HELM_JUDGMENT_STATE_LIST =
  'Triage, Todo, Scheduled, Blocked or Under Discussion' as const;
export const HELM_JUDGMENT_QUEUE_DEFINITION = `Tickets assigned to Yevhen in ${HELM_JUDGMENT_STATE_LIST} (Backlog and Duplicate excluded). Admitted to his judgment when they (a) touch a live customer or pilot title, (b) change customer-facing wording, or (c) set a methodology threshold (helm.md, 2026-08-18).`;
export const HELM_CHECKPOINT_TRIGGERS = ['schedule', 'manual'] as const; // a 'refresh' run never becomes a checkpoint
export const HELM_CARD_CAP = 32; // capped lines; critical, shipped and Slack-priority lines are exempt
export const HELM_SLACK_MENTION_LINES = 2; // then `+N more`
export const HELM_SNIPPET_MAX = 280;
export const HELM_SUBJECT_MAX = 200;
export const HELM_NOTE_MAX = 2000;
export const HELM_PERSON_WAITING_DAYS = 3;
/** A `watch` write-back sets the row's next touch this many days out. */
export const HELM_WATCH_RECHECK_DAYS = 28;
export const HELM_ACTION_HORIZON_DAYS = 60;
export const HELM_FIRST_RUN_LOOKBACK_HOURS = 72;
export const HELM_RUN_ABANDONED_MINUTES = 35;
export const HELM_DEPLOYED_LOOKBACK_DAYS = 14;
export const HELM_SWEEP_RUN_LOOKBACK_DAYS = 21;
/** The Apptonomy Slack workspace (docs/external-services/ops-slack.md). */
export const HELM_SLACK_WORKSPACE_URL = 'https://a-7-y.slack.com' as const;
/** Slack's own permalink shape for a message: the `ts` with its dot removed,
 * `p`-prefixed, under the channel. `conversations.history` returns no
 * `permalink`, so HELM builds it from the ids it already keys the item by. */
export function helmSlackPermalink(channelId: string, ts: string): string {
  return `${HELM_SLACK_WORKSPACE_URL}/archives/${channelId}/p${ts.replace('.', '')}`;
}
export const HELM_PAGE_URL = 'https://www.apptonomy.ai/admin/cmo/helm' as const; // a7y-admin/wrangler.jsonc:17 routes www.apptonomy.ai/admin*; admin-nav.ts:88
export const HELM_PIPELINE_URL =
  `https://github.com/ApptonomyInc/a7y-ai-dev/blob/main/${HELM_PIPELINE_PATH}` as const;
export const HELM_BOT = { slackUserId: 'U0AQM3X4JGZ' } as const; // a7y_bot, docs/external-services/ops-slack.md:3
export const HELM_MEASURE_WINDOW_DAYS = 14;
export const HELM_PAST_DAYS_MAX = 90;
export const HELM_RUNS_LIMIT_MAX = 300;
export const HELM_WARM_ACTIVATION_SEED = '2026-04-16' as const;
export const HELM_CARD_STYLES = ['names_and_ages', 'recommendations'] as const;
export type HelmCardStyle = (typeof HELM_CARD_STYLES)[number]; // replaces the literal union at :331 (pre-edit)
export const HELM_UNTRUSTED_TEXT_FIELDS = [
  'subject',
  'snippet',
  'title',
  'detail',
  'proposal',
  'note',
  'headline',
  'name',
  'label',
] as const;

export const HELM_SOURCE_IDS = [
  'gmail',
  'calendar',
  'slack',
  'linear',
  'git_since',
  'drive_notes',
  'linkedin_mail',
  'roster',
] as const;
export type HelmSourceId = (typeof HELM_SOURCE_IDS)[number];
export type HelmEnv = 'prod' | 'staging' | 'dev';
export type HelmTrigger = 'schedule' | 'refresh' | 'manual';
export type HelmRunStatus = 'running' | 'succeeded' | 'failed';
export type HelmFailedStage = 'collect' | 'compose' | 'publish' | 'unknown';
export type HelmSourceStatusKind = 'ok' | 'partial' | 'failed' | 'unavailable';
export type HelmErrorClass =
  | 'auth'
  | 'scope_missing'
  | 'scope_excess'
  | 'identity_mismatch'
  | 'rate_limited'
  | 'http_4xx'
  | 'http_5xx'
  | 'timeout'
  | 'parse'
  | 'not_configured'
  | 'unknown';
export interface HelmSourceRunStatus {
  status: HelmSourceStatusKind;
  windowFrom: string | null;
  windowTo: string;
  itemCount: number;
  errorClass: HelmErrorClass | null;
  httpStatus: number | null;
}

export type HelmRefKind =
  | 'gmail_message'
  | 'slack_message'
  | 'linear_issue'
  | 'calendar_event'
  | 'drive_file'
  | 'git_commit'
  | 'roster_row'
  | 'page_capture';
export interface HelmEvidence {
  source: HelmSourceId | 'page';
  observedAt: string;
  attribution: string | null;
  ref: { kind: HelmRefKind; id: string; url: string | null };
} // layer 1: observed
export interface HelmInterpretation {
  label: string;
  rule: string;
} // layer 2: proposed, rule-labelled
export const HELM_ACTION_KINDS = [
  'done',
  'defer',
  'waiting_on',
  'wrong',
  'seen',
  'capture',
  'approve',
  'reject',
] as const;
export type HelmActionKind = (typeof HELM_ACTION_KINDS)[number];
export interface HelmActor {
  userId: string;
  email: string;
  name: string;
}
export const HELM_PROPOSAL_KINDS = [
  'roster_stage_change',
  'roster_close',
  'watch_item',
  'ticket_state_mismatch',
  // A7Y-5199: a meeting booked ahead with a known contact; approval adds a roster item.
  'roster_add',
] as const;
export type HelmProposalKind = (typeof HELM_PROPOSAL_KINDS)[number];
export type HelmProposalEffect = 'bring_forward' | 'close' | 'watch' | 'add' | 'none';
export const HELM_PROPOSAL_EFFECT: Record<HelmProposalKind, HelmProposalEffect> = {
  roster_stage_change: 'bring_forward',
  roster_close: 'close',
  watch_item: 'watch',
  ticket_state_mismatch: 'none',
  roster_add: 'add',
};
/** A7Y-5199: how far ahead the calendar is read for meetings booked with a known contact (rule R6). */
export const HELM_UPCOMING_MEETING_DAYS = 14;
/** What a `roster_add` proposal adds when approved (A7Y-5199). */
export interface HelmProposalAdd {
  accountId: string;
  title: string;
  due: string;
  kind: 'touch';
  block: 'contact';
}
export type HelmProposalStatus = 'open' | 'accepted' | 'rejected' | 'stale';
export interface HelmDecision {
  actionId: string;
  kind: Exclude<HelmActionKind, 'capture'>;
  actor: HelmActor;
  at: string;
  deferUntil: string | null;
  waitingOn: string | null;
  waitingSide: 'external' | 'internal' | null;
  recheckOn: string | null;
  note: string | null;
  proposal: { key: string; kind: HelmProposalKind; effect: HelmProposalEffect } | null;
} // layer 3: accepted
export const HELM_CRITICAL_REASONS = ['live_customer', 'person_waiting', 'hard_deadline'] as const;
export type HelmCriticalReason = (typeof HELM_CRITICAL_REASONS)[number];
export interface HelmItemBase {
  key: string;
  evidence: HelmEvidence;
  interpretation: HelmInterpretation | null;
  decision: HelmDecision | null;
  critical: HelmCriticalReason[];
}

export interface HelmMeeting extends HelmItemBase {
  title: string;
  start: string;
  end: string | null;
  allDay: boolean;
  calendar: 'work' | 'shared_personal';
  /** Google Calendar `eventType` (`default`, `workingLocation`, ...); null when absent. */
  eventType: string | null;
  /** Titles of the different meetings this one overlaps (A7Y-5492: not back-to-back, never a copy of itself). */
  clashesWith: string[];
  /** Keys of the other copies of this event (another calendar's, A7Y-5492) merged into this row. */
  mergedKeys: string[];
  /** A7Y-5199: the other attendees' lower-cased email addresses; absent on runs before it. */
  attendees?: string[];
}
export interface HelmInboxItem extends HelmItemBase {
  source: 'gmail' | 'slack';
  feed: 'inbox' | 'priority' | 'mention' | 'known_contact';
  sender: { name: string | null; address: string };
  slackChannel: { id: string; label: string; kind: 'im' | 'mpim' | 'channel' } | null;
  matched: { label: string; account: string | null; rosterKey: string | null } | null;
  subject: string | null;
  snippet: string;
  receivedAt: string;
  threadRef: string;
  carried: boolean;
  answered: boolean;
  waitingDays: number | null;
  /**
   * The conversation this message LEADS: for Slack priority, the DM or channel thread whose latest open message it is;
   * for a known contact's Gmail, its `threadRef` when it is the latest open message of that thread (A7Y-5342). Null otherwise.
   */
  burstOf: string | null;
  /**
   * 1 when alone, N on the lead of N open messages, 0 on an open message folded into its lead (Slack shows it as the
   * lead's "+N more"; Gmail keeps it off Critical, so one thread is one row, A7Y-5342).
   */
  burstCount: number;
}
export interface HelmSentItem extends HelmItemBase {
  to: string[];
  subject: string | null;
  sentAt: string;
  answersKeys: string[];
  rosterKey: string | null;
}
export interface HelmLinkedInMailItem extends HelmItemBase {
  kind: 'message' | 'invitation' | 'other';
  sender: string;
  headline: string | null;
  snippet: string;
  receivedAt: string;
  /** A pending-invitations total LinkedIn itself states in the email; null when it states none. */
  statedPending: number | null;
}
export interface HelmTicketItem extends HelmItemBase {
  identifier: string;
  title: string;
  url: string;
  stateId: string;
  stateName: string;
  priority: 0 | 1 | 2 | 3 | 4;
  createdAt: string;
  ageDays: number;
  /** Linear `dueDate` (YYYY-MM-DD); null when unset. Drives `hard_deadline`. */
  dueDate: string | null;
  reasons: ('assigned' | 'urgent' | 'high' | 'blocked' | 'mention' | 'view')[];
  lastForeignCommentAt: string | null;
}
export interface HelmJudgmentQueueItem {
  key: string;
  identifier: string;
  title: string;
  url: string;
  stateName: string;
  ageDays: number;
  admittedBy: 'a' | 'b' | 'c' | null;
  admittingRule: string;
  /** Created at or after the last decision block began, exactly as the collector read it; absent on runs stored before A7Y-4733. */
  newSinceLastDecisionBlock?: boolean;
  decision: HelmDecision | null;
}
export interface HelmJudgmentQueue {
  definition: string;
  count: number;
  /** Every ticket assigned to Yevhen in the queue's states, admitted or not (A7Y-5873); absent on older runs. */
  assignedCount?: number;
  oldestDays: number | null;
  newSinceLastDecisionBlock: number;
  items: HelmJudgmentQueueItem[];
}
export interface HelmDeployedItem extends HelmItemBase {
  identifier: string;
  title: string;
  url: string;
  toStateId: string;
  toState: 'On Staging' | 'On Prod';
  enteredAt: string;
  yevhenIsAssignee: boolean;
  yevhenIsSubscriber: boolean;
  carried: boolean;
  firstSurfacedRunId: string;
  reviewPending: boolean;
  /**
   * What the shipped ticket still asks of Yevhen (A7Y-5873): assigned to him, his
   * own re-check, or a comment that asks him. Only these are named under Shipped;
   * `roster` is a run stored before A7Y-5873.
   */
  waitingOn: HelmShippedAsk | 'roster' | null;
}
export const HELM_SHIPPED_ASKS = {
  assignee: 'assigned to you',
  recheck: 'your re-check',
  mention: 'asks you in a comment',
} as const;
export type HelmShippedAsk = keyof typeof HELM_SHIPPED_ASKS;
export const HELM_COMMITMENT_STATES = [
  'actionable',
  'waiting_external',
  'waiting_internal',
  'scheduled',
  'deferred',
  'closed',
] as const;
export type HelmCommitmentState = (typeof HELM_COMMITMENT_STATES)[number];
export interface HelmCommitment extends HelmItemBase {
  table: 'prospects' | 'follow_ups' | 'scheduled_decisions' | 'closed';
  name: string;
  detail: string | null;
  /** The record item's account (A7Y-5342): what decides whether the row belongs to a live customer. Absent on runs before it. */
  accountId?: string | null;
  /** A session's suggestion for the row, never a fact (A7Y-5084); present only when the CRM item carries one. */
  proposal?: string;
  nextTouch: string | null;
  block: string | null;
  where: { text: string; href: string }[];
  state: HelmCommitmentState;
  stateRule: string;
  recheck: string | null;
  overdueDays: number | null;
  line: number;
  // The roster-derived state before any overlay is applied, so the overlay
  // can re-derive it on every pass rather than compounding on top of a
  // previous overlay's output (D9).
  derived: { state: HelmCommitmentState; recheck: string | null };
}
export interface HelmGitCommit extends HelmItemBase {
  sha: string;
  committedAt: string;
  author: string;
  subject: string;
  files: string[];
}
export type HelmCallMatchKind =
  | 'title_account'
  | 'title_roster_name'
  | 'title_known_sender'
  | 'title_live_customer'
  | 'calendar_title'
  | 'calendar_attendee';
export interface HelmCallAccountMatch {
  account: string;
  anchor: string | null;
  rosterKey: string | null;
  matchedBy: HelmCallMatchKind;
}
export interface HelmDriveNote extends HelmItemBase {
  fileId: string;
  name: string;
  modifiedAt: string;
  url: string;
  createdAt: string | null;
  meetingTitle: string | null;
  ownedBy: 'yevhen' | 'other';
  accounts: HelmCallAccountMatch[];
  /** A7Y-5199: the matched calendar event's other attendees, lower-cased; absent on runs before it. */
  attendees?: string[];
}
export interface HelmNewSender extends HelmItemBase {
  address: string;
  name: string | null;
  domain: string;
  firstSeenAt: string;
  icpScore: number;
  icpReasons: string[];
}
export interface HelmCapture extends HelmItemBase {
  note: string;
  capturedAt: string;
  actor: HelmActor;
}
export interface HelmChange {
  key: string;
  label: string;
  /** The item as Today's row named it when the change was seen (A7Y-5873); absent on older runs. */
  name?: string;
}
export interface HelmProposal extends HelmItemBase {
  // key === `proposal:${proposalId}@${rev}` (keys.ts helmItemKey.proposal)
  proposalId: string; // `${kind}:${helmItemIdentity(target.itemKey)}`
  rev: string; // helmItemRevision(target.itemKey)
  kind: HelmProposalKind;
  effect: HelmProposalEffect;
  target: { itemKey: string; label: string; url: string | null };
  proposed: string; // plain text shown as "HELM's proposal"
  basis: string[]; // layer-1 evidence item keys
  status: HelmProposalStatus;
  firstProposedRunId: string;
  movedTo: string | null; // stale only: the target's current key
  // The ISO instant `status` first became `stale` (A7Y-3872 Karen F15) —
  // `null` while the proposal has never gone stale. Lets carry-forward
  // drop a stale proposal after HELM_MEASURE_WINDOW_DAYS instead of
  // carrying it forever as long as its target identity still exists.
  staleSince: string | null;
  refused: {
    actionId: string;
    kind: 'approve' | 'reject';
    rev: string;
    at: string;
    actor: HelmActor;
  } | null;
  /** `roster_add` only (A7Y-5199): the roster item an approval adds; null or absent on every other kind. */
  add?: HelmProposalAdd | null;
}

export interface HelmFacts {
  schemaVersion: 1;
  runId: string;
  trigger: HelmTrigger;
  generatedAt: string;
  localDate: string;
  window: {
    from: string;
    to: string;
    checkpointRunId: string | null;
    widenedBecause: 'first_run' | 'missed_run' | null;
  };
  sources: Record<HelmSourceId, HelmSourceRunStatus>;
  critical: { key: string; reasons: HelmCriticalReason[] }[];
  meetings: HelmMeeting[];
  inbox: HelmInboxItem[];
  sent: HelmSentItem[];
  linkedin_mail: HelmLinkedInMailItem[];
  tickets: HelmTicketItem[];
  judgment_queue: HelmJudgmentQueue;
  deployed: HelmDeployedItem[];
  commitments: HelmCommitment[];
  roster_due: string[];
  decisions_due: string[];
  overdue: string[];
  stale: string[];
  git_since: HelmGitCommit[];
  drive_notes: HelmDriveNote[];
  new_senders: HelmNewSender[];
  captures: HelmCapture[];
  changes_since_last: { previousRunId: string | null; added: HelmChange[]; resolved: HelmChange[] };
  truncated: boolean;
  proposals: HelmProposal[];
}

export type HelmCardSection =
  | 'header'
  | 'critical'
  | 'due'
  | 'shipped'
  | 'meetings'
  | 'inbox'
  | 'slack'
  | 'judgment'
  | 'unverified'
  | 'body';
export interface HelmCardLine {
  text: string;
  keys: string[];
  section: HelmCardSection;
  critical: boolean;
  capExempt: boolean;
}
export interface HelmCard {
  style: HelmCardStyle;
  lines: HelmCardLine[];
  text: string;
  criticalCount: number;
  cappedLineCount: number;
  trimmedLineCount: number;
  droppedUnknownKeyLines: number;
}
export interface HelmCards {
  names_and_ages: HelmCard;
  recommendations: HelmCard;
  model: string;
  promptSha256: string;
  primary: HelmCardStyle;
}
export interface HelmModelCards {
  names_and_ages: { text: string; keys: string[] }[];
  recommendations: { text: string; keys: string[] }[];
}

export interface HelmScorecard {
  weekStart: string;
  asOf: string; // Madrid YYYY-MM-DD (Monday; run local date)
  decisions: { arrived: number; cleared: number; baselineRunId: string | null } | null;
  makerMornings: {
    held: number;
    leaked: number;
    pending: number;
    of: 3;
    leakedDays: string[];
  } | null;
  openBoardItems: number | null;
  warmActivation: {
    lastOn: string;
    weeksSince: number;
    evidence: 'seed' | 'new_prospect';
    prospect: string | null;
  } | null;
}
export interface HelmMeasures {
  windowDays: number;
  runsConsidered: number;
  missedCritical: { count: number; keys: string[] }; // headline; keys capped at 20
  incorrectRepeatedReminders: { count: number; identities: string[] };
  correctionPersistence: { held: number; corrections: number };
  evidenceCoverage: { linked: number; claims: number };
  delivery: {
    expectedDays: number;
    onTime: number;
    late: number;
    missed: number;
    nudgesSent: number;
    nudgesFailed: number;
  };
  freshness: {
    fresh: HelmSourceId[];
    stale: HelmSourceId[];
    partial: HelmSourceId[];
    unavailable: HelmSourceId[];
  };
}
/**
 * A7Y-5199: the record step's outcome, written on the run it ran after. It
 * re-plans every stored run since the import on each run, so `failed` is
 * retried by the next run and a line already written is never written twice.
 */
export interface HelmFactsRecordReceipt {
  status: 'written' | 'nothing_to_write' | 'failed';
  written: number;
  alreadyPresent: number;
  ambiguous: number;
  unmatched: number;
  lastTouchMoved: number;
  errorClass: string | null;
  at: string;
}
export interface HelmNudgeReceipt {
  status: 'sent' | 'skipped' | 'failed';
  reason: 'not_schedule' | 'disabled' | 'already_sent' | 'run_not_succeeded' | null;
  errorCode: string | null;
  channel: string | null;
  ts: string | null;
  at: string;
}

/** Definition text shown on the page next to each scorecard number (D10). No em-dashes. */
export const HELM_SCORECARD_DEFINITIONS: Record<
  'decisions' | 'makerMornings' | 'openBoardItems' | 'warmActivation',
  string
> = {
  decisions:
    "Tickets that entered or left your judgment queue this week (Mon to today), counted from HELM's own daily Linear snapshots.",
  makerMornings:
    'Tue, Wed and Thu mornings (10:00–13:00 Madrid) this week with no Slack message written by you. Messages in your own self-DM do not count.',
  openBoardItems:
    "Rows open in the roster's Prospects, Follow-ups owed and Scheduled decisions tables.",
  warmActivation:
    "Weeks since a new name last entered the roster's warm-activation Prospects table. The streak runs from 16 Apr 2026.",
};

/** Definition text shown on the page next to each of HELM's own measures (D11). No em-dashes. */
export const HELM_MEASURE_DEFINITIONS: Record<
  | 'missedCritical'
  | 'incorrectRepeatedReminders'
  | 'correctionPersistence'
  | 'evidenceCoverage'
  | 'delivery'
  | 'freshness',
  string
> = {
  missedCritical:
    'Critical inbox messages and shipped tickets that existed before the previous card was cut but first appeared on a later card (last 14 days).',
  incorrectRepeatedReminders: `Card lines that re-surfaced something you had already marked "That's wrong" (last 14 days).`,
  correctionPersistence: `"That's wrong" corrections that stayed off every later card (last 14 days).`,
  evidenceCoverage:
    'Card lines on the latest card that link to their source, out of every line that made a claim.',
  delivery:
    'Weekday cards published by 09:15 Madrid, late, or missing (last 14 days), and ready nudges sent or failed.',
  freshness: 'Sources read successfully on schedule, stale, partial or unavailable.',
};

export interface HelmRunDoc {
  // helm_runs/{runId}
  schemaVersion: 1;
  env: 'prod';
  runId: string;
  githubRunId: string;
  githubRunAttempt: number;
  githubRunUrl: string;
  trigger: HelmTrigger;
  status: HelmRunStatus;
  failedStage: HelmFailedStage | null;
  startedAt: string;
  completedAt: string | null;
  window: { from: string; to: string } | null;
  sources: Partial<Record<HelmSourceId, HelmSourceRunStatus>>;
  criticalCount: number | null;
  deployedKeys: string[];
  actionIdsApplied: string[];
  facts: HelmFacts | null;
  cards: HelmCards | null;
  scorecard: HelmScorecard | null;
  measures: HelmMeasures | null;
  nudge: HelmNudgeReceipt | null;
  /** A7Y-5199: what the record step wrote to HELM's record after this run; absent until it ran. */
  factsRecord?: HelmFactsRecordReceipt | null;
}
export interface HelmActionDoc {
  // helm_actions/{actionId}
  schemaVersion: 1;
  actionId: string;
  env: HelmEnv;
  itemKey: string;
  kind: HelmActionKind;
  deferUntil: string | null;
  waitingOn: string | null;
  waitingSide: 'external' | 'internal' | null;
  recheckOn: string | null;
  note: string | null;
  snapshotRunId: string | null;
  actor: HelmActor;
  createdAt: string;
  revokedAt: string | null;
  revokedBy: HelmActor | null;
}
export type HelmWritebackStatus = 'applied' | 'skipped' | 'failed';
export type HelmWritebackReason =
  'target_not_found' | 'no_roster_effect' | 'push_failed' | 'parse_failed';
export interface HelmWritebackDoc {
  // helm_writebacks/{actionId} — written only by the helm.yml write-back step
  schemaVersion: 1;
  actionId: string;
  proposalKey: string;
  kind: HelmProposalKind;
  effect: HelmProposalEffect;
  status: HelmWritebackStatus;
  reason: HelmWritebackReason | null;
  commitSha: string | null;
  path: typeof HELM_ROSTER_PATH;
  line: number | null;
  runId: string;
  at: string;
  /**
   * Every run that recorded this action as `failed`, oldest first (the doc is
   * overwritten each run, so the history rides on it). The `helm-writeback-failing`
   * sweep fires on two or more. Absent on docs written before A7Y-4733 added it.
   */
  failedRunIds?: string[];
}
export interface HelmSourceConfig {
  slack: {
    priorityPeople: { userId: string; label: string }[];
    priorityChannels: { channelId: string; label: string }[];
    mentionChannels: { channelId: string; label: string }[];
  };
  linear: { views: { viewId: string; label: string }[] };
  knownSenders: { match: string; label: string; account: string | null }[];
  liveCustomerTitles: string[];
  calendar: { sharedCalendarIds: string[]; hiddenTitleTerms: string[] };
  cardStyle: HelmCardStyle;
  readyNudge: boolean;
}
export interface HelmSourceConfigDoc extends HelmSourceConfig {
  schemaVersion: 1;
  env: HelmEnv;
  updatedAt: string;
  updatedBy: HelmActor;
} // helm_config/{env}
export const DEFAULT_HELM_SOURCE_CONFIG: HelmSourceConfig = {
  slack: {
    priorityPeople: [{ userId: 'U0AP3V90WTZ', label: 'Thomas' }],
    priorityChannels: [{ channelId: 'C0B2DRY6F1B', label: '#enric-yevhen-thomas' }],
    mentionChannels: [
      { channelId: 'C0ASAEC90DR', label: '#bd-updates' },
      { channelId: 'C0B44SC6CLQ', label: '#bd-lead-audits' },
    ],
  },
  linear: { views: [] },
  knownSenders: [],
  // A7Y-5342: the record decides who is a live customer (an account at stage `onboarded`,
  // outside the partner segment). This list only adds titles a person enters in Settings.
  liveCustomerTitles: [],
  calendar: { sharedCalendarIds: [], hiddenTitleTerms: ['Home', 'Spanish', 'Lunch'] },
  cardStyle: 'names_and_ages',
  readyNudge: true,
};

export interface HelmRunSummary {
  runId: string;
  trigger: HelmTrigger;
  status: HelmRunStatus | 'abandoned';
  failedStage: HelmFailedStage | null;
  startedAt: string;
  completedAt: string | null;
  githubRunUrl: string;
  window: { from: string; to: string } | null;
  criticalCount: number | null;
  sourceStatuses: Partial<Record<HelmSourceId, HelmSourceStatusKind>>;
  localDate: string;
  nudge: HelmNudgeReceipt['status'] | null;
}
export interface HelmSourceHealth {
  state: 'fresh' | 'stale' | 'unavailable' | 'partial';
  lastSuccessAt: string | null;
  lastAttemptAt: string | null;
  staleSince: string | null;
  lastErrorClass: HelmErrorClass | null;
}
export interface HelmRunView {
  summary: HelmRunSummary;
  facts: HelmFacts;
  cards: HelmCards;
  scorecard: HelmScorecard | null;
  measures: HelmMeasures | null;
}
export interface HelmRunDetailResponse {
  env: HelmEnv;
  run: HelmRunView;
  asOf: string;
  actions: HelmActionDoc[];
  suppressedKeys: string[];
  isCurrent: boolean;
}
export interface HelmOverviewResponse {
  env: HelmEnv;
  generatedAt: string;
  current: HelmRunView | null;
  latestAttempt: HelmRunSummary | null;
  inFlight: { runId: string; trigger: HelmTrigger; startedAt: string } | null;
  sourceHealth: Record<HelmSourceId, HelmSourceHealth>;
  actions: HelmActionDoc[];
  /** Roster write-back outcomes for the approved proposals in `actions`. */
  writebacks: HelmWritebackDoc[];
  /**
   * Keys of every item in `current.facts` currently suppressed by its
   * decision (Done/Wrong/Seen unconditionally; an active Defer/Waiting-on)
   * — computed server-side by `helmSuppressedKeys` in `actions.ts` and
   * applied here because `isHelmItemSuppressed` itself cannot reach
   * a7y-admin: its path alias resolves only this import-free `contract.ts`
   * (plan D1), never `actions.ts`. Empty when `current` is null.
   */
  suppressedKeys: string[];
}
export interface HelmRunsResponse {
  runs: HelmRunSummary[];
}
export interface HelmRefreshResponse {
  accepted: true;
  requestedAt: string;
  coalesced: boolean;
  runningSince: string | null;
}
export interface HelmActionInput {
  itemKey?: string;
  kind: HelmActionKind;
  deferUntil?: string;
  waitingOn?: string;
  waitingSide?: 'external' | 'internal';
  recheckOn?: string;
  note?: string;
  snapshotRunId?: string;
}
export interface HelmActionResponse {
  action: HelmActionDoc;
}
export interface HelmConfigResponse {
  env: HelmEnv;
  config: HelmSourceConfig;
  isDefault: boolean;
  updatedAt: string | null;
  updatedBy: HelmActor | null;
}

/* ─── Row text (A7Y-5342, A7Y-5492) ───────────────────────────────────────
 * HELM's one rule for showing a roster row: its subject, its next action in one
 * sentence, and the history behind "Show more". The run's card lines, its
 * proposals and every admin page read it from here, so the card and the page
 * cannot disagree. The record keeps the full text; nothing here shortens what
 * is stored.
 */

export const HELM_ROW_ASK_MAX = 100 as const;
export const HELM_ROW_NEXT_MAX = 140 as const;
/** What a row says when its text holds no ask (A7Y-5492). */
export const HELM_NO_NEXT_ACTION = 'no next action' as const;

/** `§9` → `section 9`: the record's section sign, spelled out on every page (A7Y-5873). */
export function helmSectionSigns(text: string): string {
  return text.replace(/§\s*/g, 'section ');
}

/**
 * A record link's label as a page shows it (A7Y-5873): a bare `§` or `pipeline §`
 * that opens an account reads "account page", any other `§` reads "section".
 */
export function helmLinkLabel(label: string, target: string): string {
  if (/^(?:pipeline\s*)?§$/.test(label.trim()))
    return /#acct-/.test(target) ? 'account page' : 'section';
  return helmSectionSigns(label);
}

/**
 * Slack message markup as plain text (A7Y-5873): `<url|label>` is its label,
 * `<url>` its URL, `<#C…|name>` `#name`, `<!here>` `@here` — also when a snippet
 * cut the closing `>` off.
 */
export function helmSlackText(text: string): string {
  return text
    .replace(/<((?:https?|mailto):[^|>\s]*)\|([^>]*)(?:>|$)/g, '$2')
    .replace(/<((?:https?|mailto):[^|>\s]*)(?:>|$)/g, '$1')
    .replace(/<#[A-Z0-9]+\|([^>]*)>/g, '#$1')
    .replace(/<!(here|channel|everyone)(?:\|[^>]*)?>/g, '@$1');
}

/** The `fallback YYYY-MM-DD` a trigger row's `where` names (A7Y-5873), or null. */
export function helmTriggerFallback(where: string | null | undefined): string | null {
  const m = /\bfallback (\d{4}-\d{2}-\d{2})\b/.exec(where ?? '');
  return m ? m[1] : null;
}

/** Markdown link text kept, URLs, emphasis, strike and code marks dropped, whitespace folded. */
export function helmPlainText(markdown: string): string {
  return markdown
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\*\*|__|~~|`/g, '')
    .replace(/(^|[\s(])_([^_]+)_(?=[\s).,;:]|$)/g, '$1$2')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Record markdown as a page prints it (A7Y-5873): `helmPlainText`, with each link
 * labelled by `helmLinkLabel` and `§` spelled out. The row summary and
 * `helmOneLine` read it; `helmPlainText` stays the record's own words for the
 * import, which compares and stores them.
 */
export function helmDisplayText(markdown: string): string {
  // A "…" the record itself carries (an import cut "against Akshit /…") is dropped
  // with the punctuation before it: a page never prints a cut fragment.
  return helmPlainText(
    helmSectionSigns(
      markdown.replace(
        /\[([^\]]*)\]\(([^)]*)\)/g,
        (_m, label: string, target: string) => `[${helmLinkLabel(label, target)}](${target})`
      )
    )
  )
    .replace(/[\s/,;:—-]*…/g, '')
    .trim();
}

/** `text` cut to `max` characters on a word boundary, with an ellipsis; `cut` says whether anything went. */
function capOnWord(text: string, max: number): { text: string; cut: boolean } {
  if (text.length <= max) return { text, cut: false };
  const head = text.slice(0, max - 1);
  const space = head.lastIndexOf(' ');
  const kept = (space > max / 2 ? head.slice(0, space) : head).replace(/[\s,;:(—-]+$/, '');
  return { text: `${kept}…`, cut: true };
}

/** One line of plain text from record markdown, capped on a word boundary (a slot's next action, A7Y-5342). */
export function helmOneLine(markdown: string, max: number = HELM_ROW_NEXT_MAX): string {
  return capOnWord(helmDisplayText(markdown), max).text;
}

export interface HelmRowSummary {
  /** Who or what the row is about: its leading bold span, or the text before the first ` (`, ` — `, `: ` or `. `, whole — never cut with "…" (A7Y-5873). */
  ask: string;
  /**
   * The row's next action in one clause (A7Y-5492, A7Y-5873), read by
   * `helmRowNextAction`; `HELM_NO_NEXT_ACTION` when the text holds none, and null
   * when the subject is itself the ask — a decision row, or a subject that opens
   * with an action.
   */
  next: string | null;
  /** True when the row holds text the summary leaves out, so a page offers "Show more". */
  more: boolean;
}

const ASK_BREAKS = [' (', ' — ', ': ', '. '];

/** A dated entry's opening mark: `**10/5:**`, `**10/8 (BD meeting, [note](…)):**`, a bold `**10/5: questionnaire submitted**`, or a plain ` — 10/5: `. */
const DATED_ENTRY_RE =
  /\*\*\d{1,2}\/\d{1,2}(?:[^*]|\*(?!\*))*?:\*\*|\*\*\d{1,2}\/\d{1,2}[^*:]{0,40}:\s|(?:^|\s—\s)\d{1,2}\/\d{1,2}(?:\s[^:—]{0,40})?:\s/g;

/**
 * Clause openings that make a clause an ask (A7Y-5492, A7Y-5873): what Yevhen or
 * Thomas does next, with or without who does it. Only verbs the record never uses
 * as nouns at a clause start — `call`, `post`, `review`, `draft` and `update` do
 * ("call note 9/4").
 */
const ACTION_OPENINGS =
  /^(?:(?:Yevhen|Thomas|Eva|Enric|we|I)\s+)?(?:follows? up|follow-up|double-checks?|tr(?:y|ies)|(?:ask|check|book|send|re-?send|repl(?:y|ie)|confirm|propose|chase|ping|nudge|decide|write|introduce|share|schedule|prepare|agree|sign|remove|find|pick|offer|re-?date|re-?engage|re-?run|read|start|move|tell|remind|give|invite|add)(?:s|es)?)\b/i;

/** The history a row keeps after `Was:` (A7Y-5873): what the row said before, never its next step. */
const WAS_HISTORY_RE = /(?:^|[\s.;—(])Was:\s[\s\S]*$/;

/** Plain text with its parentheticals dropped, innermost first, and one the text never closes too. */
function withoutParentheticals(plain: string): string {
  let text = plain;
  for (let prev = ''; prev !== text;) {
    prev = text;
    text = text.replace(/\s*\([^()]*\)/g, '');
  }
  return text
    .replace(/\s*\([^()]*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** The clauses of plain text that open with an action, in order (split at `;`, ` — `, `→`, brackets and sentence ends). */
function actionClauses(plain: string): string[] {
  return plain
    .split(/;|\s—\s|→|\(|\)|\.\s/)
    .map((c) => c.replace(/^[\s:;,.—-]+/, '').replace(/[\s,.:]+$/, ''))
    .filter((c) => ACTION_OPENINGS.test(c));
}

/**
 * The clause a row's actions name as its ask: the last of them, as entries are
 * appended oldest first, unless it is a bare two-word tail ("…; tells Thomas")
 * while a fuller one stands before it.
 */
function lastAsk(clauses: string[]): string {
  const full = clauses.filter((c) => c.split(/\s+/).length >= 3);
  return (full.length > 0 ? full : clauses)[(full.length > 0 ? full : clauses).length - 1];
}

/** The first sentence of plain text: up to the first `. ` that ends one, outside brackets. */
function firstSentence(plain: string): string {
  const end = plain.search(/[.!?](?:\s|$)(?![^(]*\))/);
  let sentence = (end >= 0 ? plain.slice(0, end) : plain).replace(/[\s(—:;,.-]+$/, '').trim();
  // A question keeps its mark ("has the CSA been re-sent and signed?").
  if (end >= 0 && plain[end] === '?') sentence = `${sentence}?`;
  // The parenthetical the entry sat in leaves its ")" unmatched at the end: drop it.
  const count = (ch: string) => sentence.split(ch).length - 1;
  while (sentence.endsWith(')') && count(')') > count('('))
    sentence = sentence.slice(0, -1).trimEnd();
  return sentence;
}

/**
 * One clause of plain text, whole (A7Y-5873: "no '…'"): its parentheticals
 * dropped, cut at its first sentence end or ` — `. A clause longer than
 * `HELM_ROW_NEXT_MAX` ends at its first `: ` after a phrase, else at its last
 * `, ` before the limit — never mid-phrase and never with an ellipsis.
 */
function oneClause(plain: string): string {
  const sentence = firstSentence(withoutParentheticals(plain));
  const dash = sentence.indexOf(' — ');
  let clause = (dash > 0 ? sentence.slice(0, dash) : sentence).trim();
  if (clause.length > HELM_ROW_NEXT_MAX) {
    // A long clause ends at a `: ` after a phrase ("decide whether they are fixed:
    // A7Y-5798 …"), never after a single label word ("review: 101 proposed").
    const colon = clause.indexOf(': ');
    if (colon > 0 && clause.slice(0, colon).trim().split(/\s+/).length >= 3)
      clause = clause.slice(0, colon);
  }
  if (clause.length > HELM_ROW_NEXT_MAX) {
    const comma = clause.lastIndexOf(', ', HELM_ROW_NEXT_MAX);
    if (comma > HELM_ROW_NEXT_MAX / 3) clause = clause.slice(0, comma);
  }
  return clause.replace(/[\s,;:—-]+$/, '');
}

/**
 * A roster row's next action, from its own text (A7Y-5492, A7Y-5873). The record
 * has no next-action field on a roster item, so the rule reads what its writers
 * put there, in order:
 *   1. the last explicit `Next:` clause;
 *   2. the latest dated entry (`— **10/5:** …`) — entries are appended oldest
 *      first, so the last is the current step: its last clause that opens with
 *      an action, else its first clause;
 *   3. the last clause outside the row's parentheticals that starts with an
 *      action (`follow up after the long weekend`);
 *   4. the clause after a `: ` that follows the subject, past an opening
 *      parenthetical (`Eva: who else she knows on the lead list`, `Kaya VC
 *      (Martin Rajcan): Yevhen double-checks the status`);
 *   5. the last clause inside a parenthetical that starts with an action;
 *   6. the clause after a ` — ` that follows the subject (`**Sergey** — touch
 *      base on next steps — Pickem`);
 *   7. a short parenthetical that is all the row holds (`justDice (pilot
 *      check-in)`) — a long one, a clause list or a link is context;
 *   8. otherwise null.
 * The caller cuts a trailing `Was: …` (the row's history) first. Steps 4, 6
 * and 7 are skipped when the subject opens with an action (`actionSubject`, "Re-run
 * and read all fourteen audits"): the text after it describes that action. A
 * decision row's subject is a question, so its colon or dash clause or short
 * parenthetical is still read ("**Zeta rollout plan** (parked until Q4)"). The
 * result never depends on whether the row is a decision — only
 * `helmRowSummary`'s fallback does — so "Show more" counts the same text
 * wherever it is asked.
 * Plain text, one clause, never an ellipsis.
 */
export function helmRowNextAction(
  rest: string,
  { actionSubject = false }: { actionSubject?: boolean } = {}
): string | null {
  const plainRest = helmDisplayText(rest);
  const nexts = [...plainRest.matchAll(/\bNext:\s*([^;]+)/g)];
  if (nexts.length > 0) {
    const clause = oneClause(nexts[nexts.length - 1][1]);
    if (clause) return clause;
  }
  const marks = [...rest.matchAll(DATED_ENTRY_RE)];
  if (marks.length > 0) {
    const last = marks[marks.length - 1];
    const entry = helmDisplayText(rest.slice((last.index ?? 0) + last[0].length));
    // The entry's own ask, when it writes one ("…; no feedback yet → ask for first
    // feedback Tue"), else its first clause: the step as it stands.
    const asks = actionClauses(entry);
    if (asks.length > 0) return oneClause(lastAsk(asks));
    const clause = oneClause(entry.replace(/^[\s—:;,.-]+/, ''));
    if (clause) return clause;
  }
  // An action outside the row's parentheticals is its ask; one inside them is
  // usually history ("(Simona asked for the change; Thomas re-sends)").
  const outside = actionClauses(withoutParentheticals(plainRest));
  if (outside.length > 0) return oneClause(lastAsk(outside));
  // A subject followed by `: <what>` names its ask there (A7Y-5873: "Cubic Frog",
  // "Deck" and "Eva — no next action" were bare names). What follows a subject
  // that is itself an action describes it; it is not a second ask.
  const colon = actionSubject ? null : /^(?:\([^()]*\)\s*)?:\s*(.+)$/.exec(plainRest);
  if (colon) {
    const what = oneClause(colon[1]);
    if (what) return what;
  }
  const inside = actionClauses(plainRest);
  if (inside.length > 0) return oneClause(lastAsk(inside));
  if (actionSubject) return null;
  // A subject followed by ` — <what>` names its ask there ("touch base on next
  // steps", "pilot check-in"); an opening parenthetical is context, never one.
  const dashed = /^—\s*(.+)$/.exec(plainRest);
  if (dashed) {
    const what = oneClause(withoutParentheticals(dashed[1]).split(/;|\s—\s/)[0]);
    if (what) return what;
  }
  // A short parenthetical that is all the row holds is the ask itself
  // ("justDice (pilot check-in)"); a long one, one of several clauses, or a
  // bare link ("([call note 9/4](…))") is context.
  const short = /^\(([^;()—]+)\)$/.exec(plainRest);
  if (short && marks.length === 0 && !/^\(\[[^\]]*\]\([^)]*\)\)$/.test(rest.trim())) {
    const what = short[1].replace(/[\s,.:]+$/, '').trim();
    if (what && what.split(/\s+/).length <= HELM_ROW_SHORT_ASK_WORDS) return what;
  }
  return null;
}

/** The most words an opening parenthetical holds and still reads as the row's ask (A7Y-5492). */
const HELM_ROW_SHORT_ASK_WORDS = 6;

/** `text` split at its first subject break (` (`, ` — `, `: `), or whole. */
function splitAtAskBreak(text: string): { lead: string; rest: string } {
  const at = ASK_BREAKS.map((b) => text.indexOf(b)).filter((i) => i > 0);
  const cut = at.length > 0 ? Math.min(...at) : -1;
  return cut > 0 ? { lead: text.slice(0, cut), rest: text.slice(cut) } : { lead: text, rest: '' };
}

export function helmRowSummary(
  markdown: string,
  { decision = false }: { decision?: boolean } = {}
): HelmRowSummary {
  const full = markdown.trim();
  const source = full.replace(WAS_HISTORY_RE, '').trim() || full;
  let lead: string;
  let rest: string;
  const bold = /^\*\*(.+?)\*\*/.exec(source);
  if (bold) {
    lead = bold[1];
    rest = source.slice(bold[0].length);
    // A bold span too long to be a subject is a sentence: its own break splits it (A7Y-5873).
    if (helmDisplayText(lead).length > HELM_ROW_ASK_MAX) {
      const split = splitAtAskBreak(lead);
      lead = split.lead;
      rest = `${split.rest}${rest}`;
    }
  } else {
    ({ lead, rest } = splitAtAskBreak(source));
  }
  // A subject still too long ends at its own `→` or last `, ` before the limit, the
  // remainder read as the row's text; one with neither shows whole, never cut with "…".
  if (helmDisplayText(lead).length > HELM_ROW_ASK_MAX && !/\]\(/.test(lead)) {
    const arrow = lead.indexOf(' → ');
    const comma = lead.lastIndexOf(', ', HELM_ROW_ASK_MAX);
    const at =
      arrow > 0 && arrow <= HELM_ROW_ASK_MAX ? arrow : comma > HELM_ROW_ASK_MAX / 3 ? comma : -1;
    if (at > 0) {
      rest = ` — ${lead.slice(at).replace(/^[\s,→]+/, '')}${rest}`;
      lead = lead.slice(0, at);
    }
  }
  const ask = helmDisplayText(lead);
  // A row whose subject is itself an action ("Re-run and read all fourteen audits"),
  // or a decision with nothing after its subject, reads alone; any other row with
  // no ask says so.
  const actionSubject = ACTION_OPENINGS.test(helmDisplayText(lead));
  const found = helmRowNextAction(rest, { actionSubject });
  const subjectIsAsk = decision || actionSubject;
  const next = found ?? (subjectIsAsk || ask === '' ? null : HELM_NO_NEXT_ACTION);
  // Anything the two lines leave out is history, behind "Show more": the row's
  // whole text against what the two lines print, marks and punctuation aside.
  const letters = (t: string) => t.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
  const more = letters(helmDisplayText(full)) !== letters(`${ask} ${found ?? ''}`);
  return { ask, next, more };
}

/**
 * The latest `m/d` date a record text names (A7Y-5492), as `YYYY-MM-DD`: a slot
 * note dated by its own words, not by the import that last saved it. A deadline
 * ("by 9/09", "until Fri 10/23") counts only when the text names no other date.
 * Dates are
 * read in the year of `writtenOn` (the record's save date, `YYYY-MM-DD…`), or
 * the year before when that would put them after it; null when the text names
 * no date.
 */
export function helmTextDate(text: string, writtenOn: string): string | null {
  const year = Number(writtenOn.slice(0, 4));
  const bound = writtenOn.slice(0, 10);
  if (!Number.isFinite(year)) return null;
  const dates: { iso: string; deadline: boolean }[] = [];
  for (const m of text.matchAll(/(?<![\d/])(\d{1,2})\/(\d{1,2})(?![\d/])/g)) {
    const month = Number(m[1]);
    const day = Number(m[2]);
    if (month < 1 || month > 12 || day < 1 || day > 31) continue;
    const md = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    let iso = `${year}-${md}`;
    if (iso > bound) iso = `${year - 1}-${md}`;
    // "nudge by 9/09" names a deadline, not the day the words were written.
    const deadline = /\b(?:by|before|until|till)\s+(?:\w{3}\s+)?$/i.test(text.slice(0, m.index));
    dates.push({ iso, deadline });
  }
  const written = dates.filter((d) => !d.deadline);
  const pool = written.length > 0 ? written : dates;
  return pool.reduce<string | null>(
    (latest, d) => (latest === null || d.iso > latest ? d.iso : latest),
    null
  );
}

/** The day of the next scheduled HELM run after `localDate` (`YYYY-MM-DD`): the next weekday (A7Y-5492). */
export function helmNextRunDay(localDate: string): string {
  const d = new Date(`${localDate}T12:00:00Z`);
  do d.setUTCDate(d.getUTCDate() + 1);
  while (d.getUTCDay() === 0 || d.getUTCDay() === 6);
  return d.toISOString().slice(0, 10);
}

/**
 * The markdown a commitment's row stands for. A cell `parseNameCell` split has a
 * plain `name` and a `detail`; one it could not split (several bold spans, or no
 * bold subject at all) keeps the whole cell, markdown included, in `name`.
 */
export function helmCommitmentMarkdown(row: { name: string; detail: string | null }): string {
  if (/\*\*|\]\(/.test(row.name)) return row.detail ? `${row.name} (${row.detail})` : row.name;
  // A name with no detail is the whole cell, plain (A7Y-5873): "Pickem: start the
  // hands-on ASO work, …" splits at its own subject break, as the record row does.
  return row.detail ? `**${row.name}** (${row.detail})` : row.name;
}

/**
 * A Critical row's name as Today's row showed it (A7Y-5873), read from the facts
 * it stood in: a roster row's subject and next action (`helmRowSummary`, the rule
 * Today reads), a message's sender and subject, a ticket's id and title. A sender
 * with no resolved name reads "name unavailable (<address>)", as Today's Slack
 * rows do — never the bare id. Null for a key the facts do not hold.
 */
export function helmCriticalRowName(
  facts: Pick<HelmFacts, 'commitments' | 'inbox' | 'tickets' | 'deployed'> | null | undefined,
  key: string
): string | null {
  if (!facts) return null;
  const commitment = (facts.commitments ?? []).find((c) => c.key === key);
  if (commitment) {
    const { ask, next } = helmRowSummary(helmCommitmentMarkdown(commitment), {
      decision: commitment.table === 'scheduled_decisions',
    });
    return next ? `${ask} — ${next}` : ask || null;
  }
  const message = (facts.inbox ?? []).find((i) => i.key === key);
  if (message) {
    const who =
      message.sender?.name || `name unavailable (${message.sender?.address ?? 'no address'})`;
    const what = message.subject || message.snippet || null;
    return what ? `${who} — ${helmSlackText(what)}` : who;
  }
  const ticket = [...(facts.tickets ?? []), ...(facts.deployed ?? [])].find((t) => t.key === key);
  return ticket ? `${ticket.identifier} — ${ticket.title}` : null;
}
